import { FC, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { useSelector } from 'react-redux'

import { ButtonLink, Card, DateTime, Table } from 'components/UI'
import { destroy, init } from 'store/actions/tables'
import { closeModal } from 'store/actions/modals'
import BaseModal from 'components/Modals/BaseModal'
import { createErrorNotification } from 'store/actions/notifications'
import { getColor } from 'services/Requests'
import { getAttendanceReasonName } from 'services/Utilities'
import Api from 'services/Api'

import { t } from 'translations'

type OverlappingAttendancesModalProps = {
  overlappingAttendances: string[]
}

const OverlappingAttendancesModal: FC<OverlappingAttendancesModalProps> = ({ overlappingAttendances }) => {
  const dispatch = useDispatch()
  const absenceBankEnabled = useSelector((state: MStore.RootState) => state.settings.absenceBankEnabled)

  const rowActions = [
    ({ _id }) => (
      <ButtonLink
        className="btn btn-outline-secondary"
        href={`/manage/attendances/${_id}`}
        text={t('manager.punch.OVERLAPPINGATTENDANCES.SEE')}
        onClick={() => dispatch(closeModal())}
      />
    )
  ]

  useEffect(() => {
    const typeMap = {
      1: 'ABSENCE',
      2: 'LATENESS',
      3: 'EARLYOUT'
    }
    async function fetchOverlappingAttendances({ filter, page }) {
      const {
        count,
        result: attendances,
        success
      }: { count: number; result: MData.Attendance[]; success: boolean } = await Api(`/requests/leave`, {
        params: {
          ...filter,
          limit: 10,
          page,
          _ids: overlappingAttendances,
          sort: '-start'
        }
      })
      if (!success) return dispatch(createErrorNotification())
      return [attendances, count]
    }
    const header = [
      {
        key: '_id',
        label: 'manager.punch.OVERLAPPINGATTENDANCES.INFORMATIONS',
        type: 'custom',
        notRemovable: true,
        render: (_, { start, end, arrivalTime, departureTime, absenceThresholdReached, requestTypeId, reasonId, requestStatusId }: MData.Attendance) => (
          <>
            <div>
              <b>{t('manager.punch.OVERLAPPINGATTENDANCES.DATE')}</b>
              <DateTime date={start} outFormat="humanDateFull" /> <DateTime date={[start, end]} range outFormat="time" />
            </div>
            <div className="d-flex flex-row">
              <b>{t('manager.punch.OVERLAPPINGATTENDANCES.TYPE')}</b>
              <div className={`${getColor(typeMap[requestTypeId])} ml-1`}>
                {t(`manager.attendances.${typeMap[requestTypeId]}`, {
                  time: arrivalTime || departureTime || ''
                })}
                {absenceThresholdReached && absenceBankEnabled ? <span className="ml-1">{t('manager.attendances.OVER_THRESHOLD')}</span> : null}
              </div>
            </div>
            <div>
              <b>{t('manager.punch.OVERLAPPINGATTENDANCES.REASON')}</b>
              {getAttendanceReasonName(reasonId)}
            </div>
            <div>
              <b>{t('manager.punch.OVERLAPPINGATTENDANCES.STATUS')}</b>
              {t(`manager.attendances.${requestStatusId}`)}
            </div>
          </>
        )
      }
    ]

    init('overlappingAttendances', header, fetchOverlappingAttendances, { defaultSortKey: '-start' })
    return () => destroy('overlappingAttendances')
  }, [absenceBankEnabled, dispatch, overlappingAttendances])

  return (
    <BaseModal title={`manager.punch.OVERLAPPINGATTENDANCES`}>
      <Card>
        <Table name="overlappingAttendances" rowActions={rowActions} />
      </Card>
    </BaseModal>
  )
}

export default OverlappingAttendancesModal
